import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ILinkedAccountInfo } from 'src/app/core/models/access';
import { IGuardian, UserAvatarData } from 'src/app/core/models/user';
import { AppState } from '../app.state';
import { GuardianState } from './guardian.reducer';

// Feature selector to select the guardian state
export const selectGuardianState = createFeatureSelector<GuardianState>('guardian');

// Selector to get the logged in guardian data
export const selectGuardianData = createSelector(
  selectGuardianState,
  (state: GuardianState) => state.guardians.find((g) => g.id === state.loggedInGuardianId) ?? ({} as IGuardian),
);

export const selectGuardians = createSelector(selectGuardianState, (state: GuardianState) => state.guardians);

export const selectGuardianLinks = createSelector(
  (state: AppState) => state.access.guardianLinks,
  (guardianLinks): ILinkedAccountInfo[] => guardianLinks ?? [],
);

// Selector to get the guardian username
export const selectGuardianUserName = createSelector(selectGuardianData, (guardian) =>
  guardian ? guardian.username : 'None',
);

export const selectSelectedGuardian = createSelector(
  selectGuardianState,
  (state: GuardianState) => state.guardians.find((g) => g.id === state.selectedGuardianId) ?? ({} as IGuardian),
);

export const selectLoggedInGuardian = createSelector(
  selectGuardianState,
  (state: GuardianState) => state.guardians.find((g) => g.id === state.loggedInGuardianId) ?? ({} as IGuardian),
);

export const selectLoadingInitialGuardian = createSelector(
  selectGuardianState,
  (state: GuardianState) => state.loadingInitialGuardian,
);

export const selectGuardianAvatar = (size: number, colour?: string) =>
  createSelector(
    selectGuardianData,
    (guardian): UserAvatarData => ({
      id: guardian?.id ?? 'no-id',
      pictureUrl: guardian?.pictureUrl ?? 'assets/images/placeholder-guardian.webp',
      isGuardian: true,
      name: guardian?.username,
      routeEnabled: true,
      borderColour: colour ?? undefined,
      size: size ?? '48',
    }),
  );

export const selectGuardianAvatarWithId = (id: string, size?: number) =>
  createSelector(selectGuardians, (guardians): UserAvatarData => {
    const guardian = guardians.find((g) => g.id === id);

    return {
      id: guardian?.id ?? 'no-id',
      pictureUrl: guardian?.pictureUrl ?? '',
      borderColour: 'white',
      isGuardian: true,
      name: guardian?.username ?? 'Unknown',
      size,
    };
  });

export const selectGuardianMissingPicture = createSelector(selectLoggedInGuardian, (guardianData) => {
  const guardianHasPicture = guardianData?.pictureUrl !== undefined && guardianData.pictureUrl !== '';
  const url = `guardians/${guardianData.id}/profile`;
  if (guardianHasPicture) {
    return { taskComplete: true, url: '', message: 'Add a profile picture for yourself.' };
  } else {
    return { taskComplete: false, url, message: 'Add a profile picture for yourself.' };
  }
});

export const selectGuardianInfoNextSteps = createSelector(selectLoggedInGuardian, (guardianData) => {
  //Check if any of the required fields are missing
  const requiredFields = [guardianData?.primaryEmail?.address, guardianData?.primaryPhone?.number];
  const missingFields = requiredFields.filter((field) => !field || field.trim() === '');
  const url = `guardians/${guardianData.id}/profile/contact-info`;

  if (missingFields.length > 0) {
    return { taskComplete: false, url, message: 'Fill out additional fields.' };
  } else {
    return { taskComplete: true, url: '', message: 'Fill out additional fields.' };
  }
});

export const selectSelectedGuardianEmail = createSelector(
  selectSelectedGuardian,
  (guardian) => guardian.email ?? 'user@email.com',
);

export const selectLoggedInGuardianLearnerAccess = createSelector(
  selectLoggedInGuardian,
  (guardian) => guardian?.learnerAccess ?? [],
);

export const selectGuardianLearnerIds = createSelector(
  selectGuardianData,
  (guardian: IGuardian) => guardian?.learnerAccess?.map((learner) => learner.learnerId) ?? [],
);

export const selectLoggedInGuardianLearnerIds = createSelector(
  selectLoggedInGuardianLearnerAccess,
  (learnerAccess) => learnerAccess.map((learner) => learner.learnerId) ?? [],
);

export const selectIsViewingOwnProfile = createSelector(
  selectSelectedGuardian,
  selectLoggedInGuardian,
  (selectedGuardian, loggedInGuardian) => {
    return selectedGuardian?.id === loggedInGuardian?.id;
  },
);

export const selectContextualGuardianName = createSelector(
  selectSelectedGuardian,
  selectIsViewingOwnProfile,
  (guardian, isViewingOwnProfile) => {
    if (isViewingOwnProfile) {
      return 'You';
    } else {
      return guardian?.username ?? 'Unknown Guardian';
    }
  },
);

export const selectLoggedInGuardianAccountLinks = createSelector(
  selectLoggedInGuardian,
  (guardian) => guardian?.accountLinks ?? [],
);

export const selectGuardianLoadingStatus = createSelector(selectGuardianState, (state: GuardianState) => state.loading);

export const selectGuardianError = createSelector(selectGuardianState, (state: GuardianState) => state.error);

export const selectLoggedInGuardianFirstStudentId = createSelector(selectLoggedInGuardianLearnerIds, (learnerIds) =>
  learnerIds.length > 0 ? learnerIds[0] : null,
);

export const selectGuardianFullName = (guardianId: string) =>
  createSelector(selectGuardians, (guardians) => {
    const guardian = guardians.find((g) => g.id === guardianId);
    return guardian ? `${guardian.firstname} ${guardian.lastname}` : 'Unknown Guardian';
  });

export const selectGuardianUserType = (guardianId?: string) =>
  createSelector(
    selectLoggedInGuardianLearnerAccess,
    selectGuardians,
    selectGuardianState,
    (loggedInLearnerAccess, guardians, state) => {
      // Use provided guardianId or fall back to selectedGuardianId
      const targetGuardianId = guardianId || state.selectedGuardianId;

      // Get the target guardian
      const targetGuardian = guardians.find((g) => g.id === targetGuardianId);
      if (!targetGuardian || !targetGuardian.learnerAccess) {
        return 'Co-Parent'; // Default to Co-Parent if guardian not found or has no access
      }

      // Get learner IDs the logged-in guardian has access to
      const loggedInLearnerIds = loggedInLearnerAccess.map((access) => access.learnerId);

      // Find common learners between logged-in guardian and target guardian
      const commonLearnerAccess = targetGuardian.learnerAccess.filter((access) =>
        loggedInLearnerIds.includes(access.learnerId),
      );

      if (commonLearnerAccess.length === 0) {
        return 'Co-Parent'; // Default to Co-Parent if no common learners
      }

      // Determine highest level of access
      // Priority: Admin > Coparent > other roles
      if (commonLearnerAccess.some((access) => access.userType === 'Admin')) {
        return 'Admin';
      } else if (
        commonLearnerAccess.some((access) => access.userType === 'Coparent' || access.userType === 'Co-Parent')
      ) {
        return 'Co-Parent';
      } else {
        return 'Co-Parent'; // Default to Co-Parent for other roles
      }
    },
  );

// Convenience selector for the selected guardian's user type
export const selectSelectedGuardianUserType = createSelector(
  selectGuardianState,
  selectLoggedInGuardianLearnerAccess,
  selectGuardians,
  (state: GuardianState, loggedInLearnerAccess, guardians) => {
    if (!state.selectedGuardianId) return 'Co-Parent';

    // Get the target guardian
    const targetGuardian = guardians.find((g) => g.id === state.selectedGuardianId);
    if (!targetGuardian || !targetGuardian.learnerAccess) {
      return 'Co-Parent'; // Default to Co-Parent if guardian not found or has no access
    }

    // Get learner IDs the logged-in guardian has access to
    const loggedInLearnerIds = loggedInLearnerAccess.map((access) => access.learnerId);

    // Find common learners between logged-in guardian and target guardian
    const commonLearnerAccess = targetGuardian.learnerAccess.filter((access) =>
      loggedInLearnerIds.includes(access.learnerId),
    );

    if (commonLearnerAccess.length === 0) {
      return 'Co-Parent'; // Default to Co-Parent if no common learners
    }

    // Determine highest level of access
    // Priority: Admin > Coparent > other roles
    if (commonLearnerAccess.some((access) => access.userType === 'Admin')) {
      return 'Admin';
    } else if (
      commonLearnerAccess.some((access) => access.userType === 'Coparent' || access.userType === 'Co-Parent')
    ) {
      return 'Co-Parent';
    } else {
      return 'Co-Parent'; // Default to Co-Parent for other roles
    }
  },
);

export const selectIsOnlyAdmin = createSelector(
  selectLoggedInGuardianLearnerAccess,
  selectGuardians,
  (loggedInLearnerAccess, allGuardians) => {
    // Get all learner IDs the logged-in guardian has access to
    const loggedInLearnerIds = loggedInLearnerAccess.map((access) => access.learnerId);

    // Check if logged-in guardian is admin for any learner
    const isLoggedInAdmin = loggedInLearnerAccess.some((access) => access.userType === 'Admin');

    if (!isLoggedInAdmin) {
      return false; // Not an admin at all
    }

    // Count total admins across all learners the logged-in guardian has access to
    let totalAdmins = 0;

    allGuardians.forEach((guardian) => {
      if (guardian.learnerAccess) {
        guardian.learnerAccess.forEach((access) => {
          // Only count admins for learners the logged-in guardian also has access to
          if (loggedInLearnerIds.includes(access.learnerId) && access.userType === 'Admin') {
            totalAdmins++;
          }
        });
      }
    });

    // Count how many learners the logged-in guardian is admin for
    const loggedInAdminCount = loggedInLearnerAccess.filter((access) => access.userType === 'Admin').length;

    // The user is the only admin if their admin count equals the total admin count
    return loggedInAdminCount === totalAdmins;
  },
);

// Selector for initial selected student IDs for access forms
export const selectInitialSelectedStudentIds = createSelector(selectSelectedGuardian, (guardian) => {
  if (!guardian?.learnerAccess) return [];
  return guardian.learnerAccess.map((access) => access.learnerId);
});

export const selectLoggedInGuardianId = createSelector(selectLoggedInGuardian, (guardian) => guardian?.id ?? null);

export const selectAllGuardianIds = createSelector(selectGuardians, (guardians) => guardians.map((g) => g.id));

export const selectGuardianAccessLevelForStudent = (studentId: string, guardianId: string) =>
  createSelector(selectGuardians, (guardians) => {
    const guardian = guardians.find((g) => g.id === guardianId);
    if (!guardian || !guardian.learnerAccess) return null;

    const access = guardian.learnerAccess.find((la) => la.learnerId === studentId);
    return access ? access.userType : null;
  });

export const selectWouldBeOnlyAdmin = createSelector(
  selectLoggedInGuardianLearnerAccess,
  selectSelectedGuardian,
  selectGuardians,
  (loggedInLearnerAccess, selectedGuardian, allGuardians) => {
    const loggedInLearnerIds = loggedInLearnerAccess.map((access) => access.learnerId);
    const selectedGuardianAccess = selectedGuardian?.learnerAccess || [];
    const selectedGuardianLearnerIds = selectedGuardianAccess.map((access) => access.learnerId);

    // Find common learner IDs between logged-in and selected guardian
    const commonLearnerIds = loggedInLearnerIds.filter((id) => selectedGuardianLearnerIds.includes(id));
    if (commonLearnerIds.length === 0) return false;

    // For each common learner, count admins
    for (const learnerId of commonLearnerIds) {
      let adminCount = 0;
      let isLoggedInAdmin = false;
      let isSelectedAdmin = false;

      allGuardians.forEach((guardian) => {
        const access = guardian.learnerAccess?.find((a) => a.learnerId === learnerId && a.userType === 'Admin');
        if (access) {
          adminCount++;
          if (guardian.id === selectedGuardian.id) isSelectedAdmin = true;
          if (loggedInLearnerAccess.some((a) => a.learnerId === learnerId && a.userType === 'Admin'))
            isLoggedInAdmin = true;
        }
      });

      // Both must be admin, and only these two
      if (!(isLoggedInAdmin && isSelectedAdmin && adminCount === 2)) {
        return false;
      }
    }

    return true;
  },
);

export const selectIsGuardiansLoadingOrEmpty = createSelector(
  selectGuardians,
  selectGuardianLoadingStatus,
  (guardians, loading) => loading || guardians.length === 0,
);

export const selectIsLoggedInUserAccountHolder = createSelector(
  selectLoggedInGuardian,
  (guardian) => guardian?.accountLinks?.some((link) => link.isAccountHolder) ?? false,
);

export const selectAccountHolderAccountLinks = createSelector(
  selectLoggedInGuardian,
  (guardian) => guardian?.accountLinks?.filter((link) => link.isAccountHolder) ?? [],
);

// Non-parameterized user type for the currently logged-in guardian.
// Mirrors the logic of selectGuardianUserType(loggedInGuardian.id) but without requiring an id at call site.
export const selectLoggedInGuardianUserType = createSelector(
  selectLoggedInGuardianLearnerAccess,
  (learnerAccess): 'Admin' | 'Co-Parent' => {
    if (!learnerAccess?.length) return 'Co-Parent';
    if (learnerAccess.some((a) => a.userType === 'Admin')) return 'Admin';
    return 'Co-Parent';
  },
);

export const selectLoggedInGuardianIsAdmin = createSelector(
  selectLoggedInGuardianUserType,
  (userType) => userType === 'Admin',
);

export const selectLoggedInGuardianIsCoParent = createSelector(
  selectLoggedInGuardianUserType,
  (userType) => userType === 'Co-Parent',
);

// Resolves the admin guardian for the logged-in co-parent.
// Prefers the account-holder admin on the co-parent's primary account; falls back to the first admin found.
export const selectAdminGuardian = createSelector(
  selectGuardians,
  selectLoggedInGuardianAccountLinks,
  selectLoggedInGuardianLearnerAccess,
  selectGuardianLinks,
  (guardians, loggedInAccountLinks, loggedInLearnerAccess, guardianLinks): IGuardian | null => {
    const loggedInLearnerIds = loggedInLearnerAccess.map((a) => a.learnerId);
    const primaryAccount = loggedInAccountLinks[0];
    const adminGuardians = guardians.filter((guardian) => {
      if (!guardian.learnerAccess) return false;
      const commonAccess = guardian.learnerAccess.filter((a) => loggedInLearnerIds.includes(a.learnerId));
      return commonAccess.some((a) => a.userType === 'Admin');
    });

    if (!adminGuardians.length) return null;
    if (adminGuardians.length === 1) return adminGuardians[0];

    const accountHolderAdmin = adminGuardians.find((g) =>
      g.accountLinks?.some(
        (link) =>
          link.accountCode === primaryAccount?.accountCode &&
          link.companyCode === primaryAccount?.companyCode &&
          !!link.isAccountHolder,
      ),
    );

    return accountHolderAdmin ?? adminGuardians[0] ?? null;
  },
);
